import robustFetch from "@/utils/robustFetch";
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu as MenuIcon,
  X as CloseIcon,
  FileImage,
  ChevronDown,
  User,
  LogOut,
  Files,
  MessageSquare,
  Shield,
  Settings,
  ScanText,
  FileText as Description,
  DollarSign as AttachMoney,
  Code,
  Mail as ContactMail,
} from "lucide-react";
import { getOrderedCategories } from "@/constants/categories";
import { UnifiedFileCount } from "@/utils/unifiedFileCount";
import { clearSessionAndCreateGuest } from "@/utils/sessionCleanup";
import ConvertMegaMenu from "@/components/ConvertMegaMenu";
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuLink,
} from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { APP_LOGO_URL } from "@/constants/branding";
import TransparentLogo from "@/components/TransparentLogo";
import GlobalSearch from "@/components/GlobalSearch";

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Initialise synchronously from localStorage to avoid CLS on first render
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    try { return !!localStorage.getItem("auth_token"); } catch { return false; }
  });
  const [user, setUser] = useState<any>(() => {
    try {
      const d = localStorage.getItem("user_data");
      return d ? JSON.parse(d) : null;
    } catch { return null; }
  });
  const [isAdmin, setIsAdmin] = useState<boolean>(() => {
    try {
      const d = localStorage.getItem("user_data");
      if (d) { const p = JSON.parse(d); return p.email === "admin@docpose.cloud"; }
    } catch {}
    return false;
  });
  const [guestFileCount, setGuestFileCount] = useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  const location = useLocation();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    const userData = localStorage.getItem("user_data");

    if (token && userData) {
      setIsAuthenticated(true);
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);

        const adminEmail = "admin@docpose.cloud";
        setIsAdmin(parsedUser.email === adminEmail);

        fetchUnreadMessageCount(token);
      } catch {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user_data");
      }
    }

    const updateFileCount = async () => {
      try {
        const result = await UnifiedFileCount.getUnifiedFileCount();
        setGuestFileCount(result.count);
      } catch (error) {
        console.debug("Navigation - Error getting file count:", error);
        setGuestFileCount(0);
      }
    };

    updateFileCount();
  }, [location, isAuthenticated]);

  const fetchUnreadMessageCount = async (token: string) => {
    try {
      const response = await robustFetch(
        "/api/user/contact-messages/stats/unread-count",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setUnreadMessageCount(data.data.unreadCount);
      }
    } catch (error) {
      console.debug("Error fetching unread message count:", error);
      setUnreadMessageCount(0);
    }
  };

  const handleLogout = () => {
    // Clear all session data and create new guest session
    clearSessionAndCreateGuest();

    // Clear component state
    setIsAuthenticated(false);
    setUser(null);
    setIsAdmin(false);
    setUnreadMessageCount(0);

    // Redirect to home (full page reload ensures fresh data and resets usage stats)
    window.location.href = "/";
  };

  const isActive = (path: string) => {
    if (!path) return false;
    return location.pathname === path;
  };

  const mainNavItems = [
    { name: "Formats", href: "/formats", icon: Description },
    { name: "Pricing", href: "/pricing", icon: AttachMoney },
    { name: "API Docs", href: "/api-docs", icon: Code },
    { name: "Contact", href: "/contact", icon: ContactMail },
  ];

  const userMenuItems = [
    { name: "Dashboard", href: "/dashboard", icon: User },
    ...(isAdmin ? [{ name: "Admin Panel", href: "/admin", icon: Shield }] : []),
    {
      name: "Messages",
      href: "/messages",
      icon: MessageSquare,
      badge: unreadMessageCount,
    },
    { name: "Subscription", href: "/subscription", icon: Settings },
  ];

  const categories = getOrderedCategories();
  const [showCategories, setShowCategories] = useState(false);

  const mobileMenuContent = (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="flex flex-col space-y-2 p-4">
        <Link to="/convert" onClick={() => setIsMenuOpen(false)}>
          <Button
            variant={isActive("/convert") ? "default" : "ghost"}
            className="w-full justify-start"
          >
            <FileImage className="mr-2 h-4 w-4" />
            Universal Converter
          </Button>
        </Link>

        <Link to="/ocr" onClick={() => setIsMenuOpen(false)}>
          <Button
            variant={isActive("/ocr") ? "default" : "ghost"}
            className="w-full justify-start"
          >
            <ScanText className="mr-2 h-4 w-4" />
            OCR Converter
          </Button>
        </Link>

        {/* Category Converters Section */}
        <div className="flex flex-col space-y-1">
          <Button
            variant="ghost"
            className="w-full justify-start text-xs font-semibold text-gray-500 hover:bg-transparent"
            onClick={() => setShowCategories(!showCategories)}
          >
            Category Converters
            <ChevronDown
              className={`ml-auto h-4 w-4 transition-transform ${
                showCategories ? "rotate-180" : ""
              }`}
            />
          </Button>

          {showCategories && (
            <div className="flex flex-col space-y-1 pl-2 border-l-2 border-gray-200">
              {categories.map((category) => {
                const IconComponent = category.icon;
                const converterUrl = `/${category.slug}-converter`;
                return (
                  <Link
                    key={category.slug}
                    to={converterUrl}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Button
                      variant={isActive(converterUrl) ? "default" : "ghost"}
                      className="w-full justify-start text-sm"
                      size="sm"
                    >
                      <IconComponent className="mr-2 h-3 w-3" />
                      {category.name} Converter
                    </Button>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        <Separator className="my-2" />

        {mainNavItems.map((item) => (
          <Link
            key={item.name}
            to={item.href}
            onClick={() => setIsMenuOpen(false)}
          >
            <Button
              variant={isActive(item.href) ? "default" : "ghost"}
              className="w-full justify-start"
            >
              <item.icon className="mr-2 h-4 w-4" />
              {item.name}
            </Button>
          </Link>
        ))}

        {!location.pathname.startsWith("/admin") && (
          <Link to="/my-files" onClick={() => setIsMenuOpen(false)}>
            <Button variant="ghost" className="w-full justify-start">
              <Files className="mr-2 h-4 w-4" />
              My Files
              {guestFileCount > 0 && (
                <Badge variant="default" className="ml-auto">
                  {guestFileCount}
                </Badge>
              )}
            </Button>
          </Link>
        )}
      </div>

      <Separator className="my-4" />

      <div className="flex flex-col space-y-4 p-4">
        {isAuthenticated ? (
          <>
            {userMenuItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setIsMenuOpen(false)}
              >
                <Button variant="ghost" className="w-full justify-start">
                  <item.icon className="mr-2 h-4 w-4" />
                  {item.name}
                  {item.badge && item.badge > 0 && (
                    <Badge variant="destructive" className="ml-auto">
                      {item.badge}
                    </Badge>
                  )}
                </Button>
              </Link>
            ))}
            <Button
              variant="ghost"
              className="w-full justify-start"
              onClick={() => {
                setIsMenuOpen(false);
                handleLogout();
              }}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </Button>
          </>
        ) : (
          <>
            <Link to="/login" onClick={() => setIsMenuOpen(false)}>
              <Button variant="ghost" className="w-full">
                Sign In
              </Button>
            </Link>
            <Link to="/signup" onClick={() => setIsMenuOpen(false)}>
              <Button variant="default" className="w-full">
                Get Started
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b bg-black backdrop-blur-md text-white overflow-visible">
        <div className="container mx-auto max-w-7xl overflow-visible">
          <div className="flex h-14 md:h-16 items-center justify-between px-4 md:px-0 overflow-visible">
            <Link to="/" className="flex items-center">
              <TransparentLogo
                src={APP_LOGO_URL}
                alt="Docpose logo"
                className="h-8 w-auto"
                imageWidth={128}
                imageHeight={32}
                loading="eager"
                decoding="sync"
              />
            </Link>

            <div className="flex flex-1 justify-center">
              {!isMobile && (
                <NavigationMenu>
                  <NavigationMenuList className="space-x-8">
                    <ConvertMegaMenu />

                    <NavigationMenuItem>
                      <NavigationMenuLink asChild>
                        <Link
                          to="/ocr"
                          className={cn(
                            "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-white/30",
                            isActive("/ocr")
                              ? "bg-white text-black font-semibold"
                              : "text-white hover:bg-white hover:text-black",
                          )}
                          aria-current={isActive("/ocr") ? "page" : undefined}
                        >
                          OCR
                        </Link>
                      </NavigationMenuLink>
                    </NavigationMenuItem>

                    {mainNavItems.map((item) => (
                      <NavigationMenuItem key={item.name}>
                        <NavigationMenuLink asChild>
                          <Link
                            to={item.href}
                            className={cn(
                              "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-white/30",
                              isActive(item.href)
                                ? "bg-white text-black font-semibold"
                                : "text-white hover:bg-white hover:text-black",
                            )}
                            aria-current={
                              isActive(item.href) ? "page" : undefined
                            }
                          >
                            {item.name}
                          </Link>
                        </NavigationMenuLink>
                      </NavigationMenuItem>
                    ))}
                  </NavigationMenuList>
                </NavigationMenu>
              )}
            </div>

            <div className="flex items-center gap-2">
              {!isMobile && <GlobalSearch />}

              {!isMobile && !location.pathname.startsWith("/admin") && (
                <Button
                  asChild
                  variant="ghost"
                  className="hover:text-black hover:bg-white"
                >
                  <Link
                    to="/my-files"
                    className="text-inherit transition-colors"
                  >
                    My Files
                    {guestFileCount > 0 && (
                      <Badge variant="default" className="ml-2">
                        {guestFileCount}
                      </Badge>
                    )}
                  </Link>
                </Button>
              )}

              {!isMobile &&
                (isAuthenticated ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="hover:text-black hover:bg-white"
                      >
                        <User className="mr-2 h-4 w-4" />
                        {user?.firstName || "User"}
                        <ChevronDown className="ml-2 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>My Account</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {userMenuItems.map((item) => (
                        <DropdownMenuItem key={item.name} asChild>
                          <Link to={item.href} className="flex items-center">
                            <item.icon className="mr-2 h-4 w-4" />
                            {item.name}
                            {item.badge && item.badge > 0 && (
                              <Badge variant="destructive" className="ml-auto">
                                {item.badge}
                              </Badge>
                            )}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout}>
                        <LogOut className="mr-2 h-4 w-4" />
                        Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      asChild
                      variant="ghost"
                      className="hover:text-black hover:bg-white"
                    >
                      <Link
                        to="/login"
                        className="text-inherit transition-colors"
                      >
                        Sign In
                      </Link>
                    </Button>
                    <Button asChild variant="default" size="sm">
                      <Link to="/signup">Get Started</Link>
                    </Button>
                  </div>
                ))}

              {isMobile && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                  aria-expanded={isMenuOpen}
                >
                  {isMenuOpen ? (
                    <CloseIcon className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <MenuIcon className="h-5 w-5" aria-hidden="true" />
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <SheetContent side="right" className="w-[280px] sm:w-[350px]">
          <SheetHeader>
            <SheetTitle>
              <Link to="/" className="flex items-center">
                <TransparentLogo
                  src={APP_LOGO_URL}
                  alt="Docpose logo"
                  className="h-8 w-auto"
                  imageWidth={128}
                  imageHeight={32}
                />
              </Link>
            </SheetTitle>
          </SheetHeader>
          {mobileMenuContent}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default Navigation;
